import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

interface EpxDialysisData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  remindDayBefore: boolean;
  isInteractive: boolean;
  shouldAskReasons: boolean;
  meta?: {
    treatmentAt?: string;
  };
}

type EpxDialysisRequestData = Omit<EpxDialysisData, 'days'> & {
  days: string;
};

type EpxDialysisDataRaw = DateSerialized<EpxDialysisData, 'start' | 'end'> & {
  treatmentDays: number[];
};

const DATE_FORMAT = 'YYYY-MM-DD';

export const EpxDialysisDefaultData: EpxDialysisData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00'],
  days: [1, 3, 5],
  remindDayBefore: false,
  isInteractive: true,
  shouldAskReasons: false,
  meta: {
    treatmentAt: '10:00'
  }
};

const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxDialysisFormProps {
  setForm?: (form: FormInstance<EpxDialysisDataRaw>) => void;
  onSubmit?: (data: EpxDialysisRequestData) => void;
  isDisabled?: boolean;
}

const EpxDialysisForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxDialysisFormProps): JSX.Element => {
  const serialize = (data: EpxDialysisDataRaw): EpxDialysisData => {
    let days = data.treatmentDays;
    if (data.remindDayBefore) {
      days = [];
      for (const day of data.treatmentDays) {
        days.push(day === 0 ? 6 : day - 1);
      }
    }
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      days: days
    };
  };
  const deserialize = (data: EpxDialysisData): EpxDialysisDataRaw => {
    let treatmentDays = data.days;
    if (data.remindDayBefore) {
      treatmentDays = [];
      for (const day of data.days) {
        treatmentDays.push((day + 1) % 7);
      }
    }
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      treatmentDays
    };
  };
  const defaultData = deserialize(EpxDialysisDefaultData);
  const form = new FormInstance<EpxDialysisDataRaw, EpxDialysisData>(
    defaultData,
    {
      serialize,
      deserialize
    }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxDialysisData): void => {
    _onSubmit({
      ...data,
      days: data.days.join(',')
    });
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectMethod />
            </Col>
            <Col sm={12}>
              <SelectLanguage />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <StartDate label="Start Date" name="start" fill />
            </Col>
            <Col sm={12}>
              <EndDate label="End Date" name="end" />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Timezone label="Timezone" name="tz" fill />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <Weekday
                label="Treatment Days"
                name="treatmentDays"
                minItems={1}
                inline
                abbreviated
              />
            </Col>
            <Col sm={12}>
              <SelectTime label="Treatment Time" name="meta.treatmentAt" />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectBooleanInput
                label="Reminder On"
                name="remindDayBefore"
                trueLabel="Day before Treatment"
                falseLabel="Same Day as Treatment"
                fill
              />
            </Col>
            <Col sm={12}>
              <SelectTime label="Reminder Time" name="times[0]" />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <SelectBooleanInput
                label="Ask about Attendance"
                name="isInteractive"
                fill
              />
            </Col>
          </Row>
        </CardBlock>
      </Form>
    </CardBlock>
  );
};

export default EpxDialysisForm;
